if (!customElements.get('pitw-carousel')) {
  class PitwCarousel extends HTMLElement {
    async connectedCallback() {
      await customElements.whenDefined('swiper-container');

      this._el = this.querySelector('swiper-container');
      if (!this._el) return;

      // Parse Swiper options from HTML attributes and set as JS properties.
      // Swiper Web Components read simple attributes automatically, but JSON
      // attributes (breakpoints, injectStyles) must be set as properties.
      const opts = {
        a11y: false,
        keyboard: { enabled: true, onlyInViewport: false },
        preventClicksPropagation: false,
        preventClicks: false,
      };

      [
        ['slides-per-view',      'slidesPerView',      parseFloat],
        ['space-between',        'spaceBetween',        parseInt],
        ['grab-cursor',          'grabCursor',          v => v !== 'false'],
        ['loop',                 'loop',                v => v !== 'false'],
        ['centered-slides',      'centeredSlides',      v => v !== 'false'],
        ['slides-offset-before', 'slidesOffsetBefore',  parseInt],
      ].forEach(([attr, key, parse]) => {
        if (this._el.hasAttribute(attr)) opts[key] = parse(this._el.getAttribute(attr));
      });

      const bp = this._el.getAttribute('breakpoints');
      if (bp) try { opts.breakpoints = JSON.parse(bp); } catch (_) {}

      const is = this._el.getAttribute('inject-styles');
      if (is) try { opts.injectStyles = JSON.parse(is); } catch (_) {}

      Object.assign(this._el, opts);
      this._el.initialize();

      await new Promise(r => requestAnimationFrame(r));
      if (!this._el.swiper) return;

      this._swiper = this._el.swiper;
      this._timer  = null;

      this._swiper.slideToLoop(0, 0);
      this._revealPeekSlides();
      this._attachTiles();
      this._attachSnippet();
      this._attachA11y();
    }

    disconnectedCallback() {
      clearTimeout(this._timer);
    }

    // ── Tile click handling ──────────────────────────────────────────────────

    _attachTiles() {
      // Direct listeners on every tile (originals + Swiper loop clones).
      // At this point requestAnimationFrame has fired after initialize(), so
      // all clones already exist in the DOM.
      // No drag-detection guard needed: mobile browsers natively suppress click
      // events after a real swipe, so accidental plays after swiping can't occur.
      this._el.querySelectorAll('[data-play-video]').forEach(tile => {
        tile.addEventListener('click', e => {
          const video = tile.querySelector('[data-hover-video]');
          if (!video) return;
          e.preventDefault();
          // Treat both manual play and auto-preview as "already playing" for pause
          const isActive = tile.classList.contains('is-playing')
                        || tile.classList.contains('is-previewing');
          isActive ? this._pause(tile, video) : this._play(tile, video);
        });
      });
    }

    // ── Auto-preview snippet ─────────────────────────────────────────────────

    _attachSnippet() {
      const ms = parseInt(this._el.getAttribute('data-snippet-ms'), 10) || 0;
      if (!ms) return;
      this._snippetMs = ms;

      new IntersectionObserver(entries => {
        entries[0].isIntersecting ? this._playActive() : this._stopAll();
      }, { threshold: 0.2 }).observe(this._el);
    }

    // ── Keyboard / focus a11y ────────────────────────────────────────────────

    _attachA11y() {
      this._swiper.once('touchStart', () => {
        this._el.querySelectorAll('swiper-slide > *:first-child').forEach(s => {
          s.classList.remove('animate-reveal-up-short');
          s.style.opacity = '';
        });
      });

      this._el.querySelectorAll('[data-hover-tile]').forEach(tile => {
        tile.addEventListener('focus', () => {
          const slide = tile.closest('swiper-slide');
          if (!slide) return;
          const idx = parseInt(slide.dataset.swiperSlideIndex, 10);
          if (!isNaN(idx)) this._swiper.slideToLoop(idx, 300);
        });
      });

      this._swiper.on('keyPress', (swiper, keyCode) => {
        if (keyCode !== 37 && keyCode !== 39) return;
        this._swiper.once('slideChangeTransitionEnd', () => {
          const tile = this._swiper.slides[this._swiper.activeIndex]
            ?.querySelector('[data-hover-tile]');
          if (tile) tile.focus({ preventScroll: true });
        });
      });
    }

    // ── Video helpers ────────────────────────────────────────────────────────

    _play(tile, video) {
      this._stopAll();
      video.muted = true;

      // Set video.src as a property before play() — this is the pattern iOS Safari
      // requires. Using only <source> elements without a src property causes play()
      // to fail silently. Only load once; subsequent plays just resume.
      if (!video._pitwSrcLoaded) {
        const src = video.dataset.src
          || video.querySelector('source')?.getAttribute('src');
        if (src) video.src = src;
        video._pitwSrcLoaded = true;
      }

      video.play().catch(() => {});
      tile.classList.add('is-playing');
      if (tile.hasAttribute('aria-label')) tile.setAttribute('aria-label', 'Pause video');
    }

    _pause(tile, video) {
      video.pause();
      video.currentTime = 0;
      tile.classList.remove('is-playing', 'is-previewing');
      if (tile.hasAttribute('aria-label')) tile.setAttribute('aria-label', 'Play video');
    }

    _stopAll() {
      clearTimeout(this._timer);
      this._el.querySelectorAll('[data-hover-video]').forEach(v => {
        v.pause();
        v.currentTime = 0;
      });
      this._el.querySelectorAll('[data-play-video].is-playing, [data-play-video].is-previewing').forEach(t => {
        t.classList.remove('is-playing', 'is-previewing');
        if (t.hasAttribute('aria-label')) t.setAttribute('aria-label', 'Play video');
      });
    }

    _playActive() {
      const slide = this._swiper.slides[this._swiper.activeIndex];
      const video = slide?.querySelector('[data-hover-video]');
      const tile  = slide?.querySelector('[data-play-video]');

      // Play video if this slide has one; image-only slides just wait out the timer.
      if (video && tile) {
        // Use _play() to handle src loading, then swap to is-previewing so the
        // play icon stays visible (is-playing hides it, is-previewing doesn't)
        this._play(tile, video);
        tile.classList.replace('is-playing', 'is-previewing');
        if (tile.hasAttribute('aria-label')) tile.setAttribute('aria-label', 'Play video');
      }

      // Always schedule the advance regardless of whether the slide has a video.
      this._timer = setTimeout(() => {
        if (this._el.hasAttribute('data-snippet-advance')) {
          this._swiper.once('slideChangeTransitionEnd', () => {
            this._stopAll();
            this._playActive();
          });
          this._swiper.slideNext();
        } else {
          this._stopAll();
        }
      }, this._snippetMs);
    }

    _revealPeekSlides() {
      setTimeout(() => {
        this._el.querySelectorAll('swiper-slide > *:first-child').forEach(s => {
          const r = s.getBoundingClientRect();
          if (r.right > 0 && r.left < window.innerWidth) s.style.opacity = '';
        });
      }, 300);
    }
  }

  customElements.define('pitw-carousel', PitwCarousel);
}
